import { useEffect, useState, useRef } from 'react';
import debounce from 'lodash.debounce';

import Container from '/components/Container';
import JobQuestions from './JobQuestions';
import { windowHeight } from '/lib/helpers';
import useUserCan from '/hooks/useUserCan';
import { trackViewItem } from '/lib/ga-data-layer';
import { useUserState } from '/store/userStore';
import { JobTemplate, JobDestinations, Answer } from '/types/jobTemplateTypes';
import Draft from '/types/draftType';

import FloatingPriceInfo from './FloatingPriceInfo';
import SubmissionSection from './SubmissionSection';
import useJobConfiguration from '/hooks/useJobConfiguration';
import JobInfo from './JobInfo';
import JobMaterials from './JobMaterials';
import JobImages from './JobImages';
import JobImportantNote from './JobImportantNote';
import MissingImagesModal from './MissingImagesModal';
import SellerConfirmationModal from './SellerConfirmationModal';

interface Props {
    job: JobTemplate;
    jobDestination: JobDestinations;
    draft?: Draft;
    withMaterials?: boolean;
}

const JobConfigurator = ({
    job,
    jobDestination,
    draft,
    withMaterials,
}: Props) => {
    const user = useUserState((state) => state.user);
    const userCan = useUserCan();

    const {
        isJobFromCart,
        isJobFromDraft,
        missingImagesModalVisible,
        sellerConfirmationModalVisible,
        submitConfiguration,
        jobData,
        setters,
    } = useJobConfiguration({ job, jobDestination, draft });

    const submissionSectionRef = useRef<HTMLDivElement>(null);

    const [submissionSectionVisibility, setSubmissionSectionVisibility] =
        useState({
            entered: false,
            enteredCompletely: false,
        });

    const onScroll = () => {
        if (submissionSectionRef.current) {
            const winH = windowHeight();
            const bcr = submissionSectionRef.current.getBoundingClientRect();
            setSubmissionSectionVisibility({
                entered: bcr.top < winH,
                enteredCompletely: winH - bcr.height > bcr.top,
            });
        }
    };

    useEffect(() => {
        trackViewItem(job);
        window.addEventListener('scroll', debounce(onScroll, 10));
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const onAnswerChange = async (questionId: string, newAnswer: Answer) => {
        const updatedAnswers = jobData.answers.find(
            (a) => a.questionId === questionId
        )
            ? jobData.answers.map((a) =>
                  a.questionId === questionId ? newAnswer : a
              )
            : [...jobData.answers, newAnswer];

        setters.setAnswers(updatedAnswers);
    };

    return (
        <>
            {missingImagesModalVisible && (
                <MissingImagesModal
                    onClose={() => setters.setMissingImagesModalVisible(false)}
                />
            )}

            {sellerConfirmationModalVisible && (
                <SellerConfirmationModal
                    onClose={() =>
                        setters.setSellerConfirmationModalVisible(false)
                    }
                    onSubmit={(revisionRequired) => {
                        setters.setRevisionRequired(revisionRequired);
                        return submitConfiguration();
                    }}
                />
            )}

            <Container small>
                <JobInfo
                    title={jobData.info.title || job.jobTypeVersion.name}
                    info={jobData.info}
                    totals={jobData.totals}
                    showExternalProviderInfo={job.integrationJob}
                    onCallToActionButtonClick={() =>
                        document
                            ?.getElementById('job-questions')
                            ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            })
                    }
                />

                <JobQuestions
                    questions={jobData.questions}
                    answers={jobData.answers}
                    onAnswerChange={onAnswerChange}
                    materialsEdited={jobData.materialsEdited}
                    hideProductSelector={
                        !(draft && userCan.changeProjectCaseworker) &&
                        job.integrationJob
                    }
                    showSelectedProductInProductSelector={
                        !(draft && userCan.changeProjectCaseworker)
                    }
                />

                <JobImportantNote
                    importantNote={jobData.importantNote}
                    onChange={setters.setImportantNote}
                    jobDestination={jobDestination}
                />

                {jobDestination !== 'cart' && userCan.editTemplateJobImages && (
                    <JobImages
                        images={jobData.images}
                        onChange={setters.setImages}
                    />
                )}

                {user && withMaterials && (
                    <JobMaterials
                        materials={jobData.materials}
                        onChange={(materials) => {
                            setters.setEditedMaterialList(materials);
                            setters.setMaterials(materials);
                            setters.setMaterialsEdited(true);
                        }}
                    />
                )}
            </Container>

            <div ref={submissionSectionRef}>
                <SubmissionSection
                    jobName={job.jobTypeVersion.name}
                    jobType={job.typeKey}
                    totalCost={jobData.totals.totalCost}
                    destination={jobDestination}
                    jobFromCart={isJobFromCart}
                    jobFromDraft={isJobFromDraft}
                    onSubmit={submitConfiguration}
                />
            </div>

            <FloatingPriceInfo
                isVisible={!submissionSectionVisibility.entered}
                jobName={job.jobTypeVersion.name}
                totalCost={jobData.totals.totalCost}
            />
        </>
    );
};

export default JobConfigurator;
